import React, { useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { translateLocation } from '../../utils/translationUtils';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import UserAvatar from '../../components/UserAvatar';
import './AdminLayout.css';

const AdminLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { data } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedLocation, setSelectedLocation] = useState(
    user?.role === 'admin' ? 'all' : String(user?.location || 'all').toLowerCase()
  );
  const [navQuery, setNavQuery] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleTitle = () => {
    switch (user.role) {
      case 'admin':
        return t('roles.admin');
      case 'manager':
        return t('roles.manager');
      case 'instructor':
        return t('roles.instructor');
      default:
        return t('roles.student');
    }
  };

  const role = user?.role;
  const allNavLinks = useMemo(() => ([
    {
      path: '/admin',
      label: t('admin.dashboard'),
      exact: true,
      section: 'overview',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['dashboard', 'start', 'home']
    },
    {
      path: '/admin/studies',
      label: t('admin.viewHierarchy'),
      section: 'operations',
      roles: ['admin', 'manager'],
      keywords: ['studies', 'hierarchy', 'jaar', 'module']
    },
    {
      path: '/admin/study-registrations',
      label: t('admin.studyRegistrations'),
      section: 'operations',
      roles: ['admin', 'manager'],
      keywords: ['registrations', 'inschrijvingen', 'studie']
    },
    {
      path: '/admin/course-assignments',
      label: t('admin.courseAssignments'),
      section: 'operations',
      roles: ['admin', 'manager'],
      keywords: ['course', 'assignments', 'vak', 'toewijzing']
    },
    {
      path: '/admin/homework',
      label: t('nav.homework'),
      section: 'teaching',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['homework', 'opdracht', 'assignment']
    },
    {
      path: '/admin/module-materials',
      label: t('admin.moduleMaterials'),
      section: 'teaching',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['materials', 'documenten', 'bestanden', 'upload']
    },
    {
      path: '/admin/module-evaluations',
      label: 'Module evaluaties',
      section: 'teaching',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['evaluatie', 'feedback', 'module']
    },
    {
      path: '/admin/exam-component-uploads',
      label: 'Examen uploads',
      section: 'teaching',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['beroepsproduct', 'adviesrapport', 'examen', 'upload']
    },
    {
      path: '/admin/calendar',
      label: t('admin.calendar'),
      section: 'teaching',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['calendar', 'agenda', 'planning']
    },
    {
      path: '/admin/payments',
      label: t('admin.payments'),
      section: 'management',
      roles: ['admin', 'manager'],
      keywords: ['payments', 'betaling', 'factuur']
    },
    {
      path: '/admin/users',
      label: t('admin.users'),
      section: 'management',
      roles: ['admin', 'manager'],
      keywords: ['users', 'gebruikers', 'rollen']
    },
    {
      path: '/admin/cohorts',
      label: t('admin.cohorts'),
      section: 'management',
      roles: ['admin', 'manager'],
      keywords: ['cohorts', 'groepen']
    },
    {
      path: '/admin/departments',
      label: t('admin.departments'),
      section: 'management',
      roles: ['admin', 'manager'],
      keywords: ['departments', 'locaties']
    },
    {
      path: '/admin/content',
      label: t('admin.websiteContent'),
      section: 'content',
      roles: ['admin', 'manager'],
      keywords: ['content', 'website', 'cms', 'brochures', 'blogs']
    },
    {
      path: '/admin/notifications',
      label: t('admin.notifications'),
      section: 'content',
      roles: ['admin', 'manager'],
      keywords: ['notifications', 'meldingen']
    },
    {
      path: '/admin/declarations',
      label: t('admin.declarations'),
      section: 'content',
      roles: ['admin', 'manager'],
      keywords: ['declarations', 'verklaringen']
    },
    {
      path: '/admin/report-payments',
      label: t('report.paymentReport', 'Betalingen Rapport'),
      section: 'reports',
      roles: ['admin', 'manager'],
      keywords: ['report', 'betalingen', 'payments']
    },
    {
      path: '/admin/report-grades',
      label: t('report.gradesReport', 'Cijfers Rapport'),
      section: 'reports',
      roles: ['admin', 'manager', 'instructor'],
      keywords: ['report', 'grades', 'cijfers']
    }
  ]), [t]);

  const roleLinks = useMemo(() => allNavLinks.filter((link) => link.roles.includes(role)), [allNavLinks, role]);

  const filteredLinks = useMemo(() => {
    const query = navQuery.trim().toLowerCase();
    if (!query) return roleLinks;
    return roleLinks.filter((link) => {
      const haystack = [link.label, ...(link.keywords || [])].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [navQuery, roleLinks]);

  const groupedLinks = useMemo(() => ({
    overview: filteredLinks.filter((link) => link.section === 'overview'),
    operations: filteredLinks.filter((link) => link.section === 'operations'),
    teaching: filteredLinks.filter((link) => link.section === 'teaching'),
    management: filteredLinks.filter((link) => link.section === 'management'),
    content: filteredLinks.filter((link) => link.section === 'content'),
    reports: filteredLinks.filter((link) => link.section === 'reports')
  }), [filteredLinks]);

  const currentPageLabel = useMemo(() => {
    const exact = roleLinks.find((link) => link.exact && location.pathname === link.path);
    if (exact) return exact.label;
    const fuzzy = roleLinks.find((link) => !link.exact && location.pathname.startsWith(link.path));
    return fuzzy?.label || t('admin.dashboard');
  }, [location.pathname, roleLinks, t]);

  const quickLinks = useMemo(() => roleLinks.slice(0, 3), [roleLinks]);

  const renderNavGroup = (title, links) => {
    if (!links.length) return null;
    return (
      <div className="admin-nav-group" key={title}>
        <p className="admin-nav-group-title">{title}</p>
        {links.map((link) => (
          <NavLink
            key={link.path}
            to={link.path}
            end={link.exact}
            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
          >
            {link.label}
          </NavLink>
        ))}
      </div>
    );
  };

  const locations = [
    { id: 'all', name: t('admin.allLocations') },
    ...(data?.locations || [])
  ];
  const displayName = formatUserDisplayName(user);

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <UserAvatar user={user} size="lg" className="sidebar-avatar" />
          <h2>{t('admin.title')}</h2>
          <p className="user-role">{getRoleTitle()}</p>
          <p className="user-name">{displayName}</p>
          {user.location && <p className="user-location-chip">{translateLocation(t, String(user.location).toLowerCase())}</p>}
          <input
            type="search"
            className="admin-nav-search"
            placeholder={t('common.search')}
            value={navQuery}
            onChange={(event) => setNavQuery(event.target.value)}
          />
        </div>

        {/* Location Filter */}
        {user.role === 'admin' && (
          <div className="location-filter">
            <label>{t('admin.location')}</label>
            <select 
              value={selectedLocation} 
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.id === 'all' ? loc.name : translateLocation(t, loc.id)}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="sidebar-nav">
          {renderNavGroup(t('common.dashboard'), groupedLinks.overview)}
          {renderNavGroup(t('admin.quickActions'), groupedLinks.operations)}
          {renderNavGroup(t('nav.homework'), groupedLinks.teaching)}
          {renderNavGroup(t('admin.users'), groupedLinks.management)}
          {renderNavGroup(t('admin.websiteContent'), groupedLinks.content)}
          {renderNavGroup(t('admin.reports'), groupedLinks.reports)}
          {filteredLinks.length === 0 && (
            <p className="admin-nav-empty">{t('common.noData')}</p>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            {t('nav.logout')}
          </button>
          <p>NLBA LMS</p>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-main-topbar">
          <strong>{currentPageLabel}</strong>
          <div className="admin-main-quicklinks">
            {quickLinks.map((link) => (
              <NavLink key={link.path} to={link.path} end={link.exact} className="admin-quicklink">
                {link.label}
              </NavLink>
            ))}
          </div>
        </div>
        <Outlet context={{ selectedLocation }} />
      </main>
    </div>
  );
};

export default AdminLayout;
